import { type FC, useState, useMemo, useContext, useEffect, useCallback } from "react";
import { NotAuthorized } from "@/components/NotAuthorized";
import { LoadingState } from "@/components/podkit/loading/LoadingState";
import { Button } from "@/components/flexkit/Button";
import { IconDownload } from "@/assets/icons/geist/IconDownload";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { formatError } from "@/utils/errors";
import { useDocumentTitle } from "@/hooks/use-document-title";
import { getPrincipal } from "@/principal";
import { useMembership } from "@/hooks/use-membership";
import { useOrganization } from "@/queries/organization-queries";
import { useEnterpriseCreditUsageReport, useExportCreditUsage } from "@/queries/enterprise-usage-queries";
import { OrganizationRole } from "gitpod-next-api/gitpod/v1/organization_pb";
import { cn } from "@/components/podkit/lib/cn";
import { Text } from "@/components/podkit/typography/Text";
import { Code, ConnectError } from "@connectrpc/connect";
import { Select, SelectContent, SelectItem, SelectTrigger } from "@/components/podkit/select/Select";
import { EnterpriseUsageDailyChart } from "@/routes/settings/organization/enterprise-usage/EnterpriseUsageDailyChart";
import { ExportCsvModal } from "@/routes/settings/organization/enterprise-usage/ExportCsvModal";
import { SettingsPageContext } from "@/contexts/SettingsPageContext";
import {
    buildMonthRanges,
    buildDailyChartData,
    buildTeamDailyChartData,
    computeTopUsers,
    computeTotalCredits,
    extractUniqueUsers,
    formatCredits,
    formatCreditsExact,
    formatUpdatedAt,
    GROUPING_VALUES,
    USAGE_TYPE_VALUES,
    type Grouping,
    type UsageTypeFilter,
} from "@/routes/settings/organization/enterprise-usage/enterprise-usage-helpers";
import { parseAsStringLiteral, useQueryState } from "nuqs";

import { TeamUsageTable } from "@/routes/settings/organization/enterprise-usage/TeamUsageTable";
import { useCumulativeCreditUsage } from "@/queries/credit-usage-queries";
import { SkeletonText } from "@/components/podkit/loading/Skeleton";
import { useFlagTeamsEnabled } from "@/hooks/use-flag";

const EnterpriseUsagePage: FC = () => {
    useDocumentTitle("Usage");

    const { setBreadCrumbRowAction } = useContext(SettingsPageContext);

    const { data: organization, isLoading: isLoadingOrganization } = useOrganization();
    const { membership, isPending: isLoadingMembership } = useMembership();

    // Build the full set of month ranges first (no period filtering yet).
    const allMonthRanges = useMemo(() => buildMonthRanges(), []);
    const allMonthValues = useMemo(() => allMonthRanges.map((r) => r.value), [allMonthRanges]);
    const [selectedMonth, setSelectedMonth] = useQueryState(
        "month",
        parseAsStringLiteral(allMonthValues).withDefault(allMonthValues[0] ?? ""),
    );
    const [selectedType, setSelectedType] = useQueryState(
        "type",
        parseAsStringLiteral(USAGE_TYPE_VALUES).withDefault("all"),
    );
    const [grouping, setGrouping] = useQueryState(
        "grouping",
        parseAsStringLiteral(GROUPING_VALUES).withDefault("users"),
    );

    const organizationId = organization?.id ?? "";
    const activeRange = useMemo(
        () => allMonthRanges.find((r) => r.value === selectedMonth) ?? allMonthRanges[0],
        [allMonthRanges, selectedMonth],
    );

    const {
        data: reportData,
        isLoading: isLoadingReport,
        error,
    } = useEnterpriseCreditUsageReport(organizationId, activeRange.start, activeRange.end);

    const { value: teamsEnabled } = useFlagTeamsEnabled();

    // For past months pass the month end as `asOf` so the cumulative total
    // reflects usage up to that point. For the current month omit it so the
    // backend defaults to "now".
    const isCurrentMonthSelected = activeRange.value === allMonthRanges[0]?.value;
    const cumulativeAsOf = isCurrentMonthSelected ? undefined : activeRange.end;

    const {
        data: cumulativeData,
        isLoading: isLoadingCumulative,
        error: cumulativeError,
    } = useCumulativeCreditUsage(organizationId, cumulativeAsOf);

    // Derive period start from the report response to filter out earlier months.
    // Memoize on the raw seconds value so that React Query background refetches
    // returning the same timestamp don't create a new Date identity.
    const periodStartSeconds = reportData?.periodStart?.seconds;
    const reportPeriodStart = useMemo(
        () => (periodStartSeconds != null ? new Date(Number(periodStartSeconds) * 1000) : undefined),
        [periodStartSeconds],
    );

    // new Date() is intentionally not a dependency — the reference date only
    // matters at month granularity, so re-computing when reportPeriodStart
    // changes is sufficient.
    const monthRanges = useMemo(
        () => buildMonthRanges(new Date(), reportPeriodStart),
        [reportPeriodStart],
    );

    // Keep selectedMonth in sync when the filtered ranges change.
    // When the filtered list is empty (period start is in the future) fall back
    // to the first unfiltered range so the page still renders a valid month.
    useEffect(() => {
        if (monthRanges.length > 0 && !monthRanges.some((r) => r.value === selectedMonth)) {
            void setSelectedMonth(monthRanges[0].value);
        } else if (monthRanges.length === 0 && allMonthRanges.length > 0 && selectedMonth !== allMonthRanges[0].value) {
            void setSelectedMonth(allMonthRanges[0].value);
        }
    }, [monthRanges, allMonthRanges, selectedMonth, setSelectedMonth]);

    const updatedAt = isCurrentMonthSelected ? formatUpdatedAt(reportData?.updatedAt) : "";

    // Always show the full calendar month on the chart, even when the API
    // range ends mid-month (current month). This avoids a nearly-empty chart
    // early in the month.
    const chartMonthEnd = useMemo(() => {
        const s = activeRange.start;
        return new Date(Date.UTC(s.getUTCFullYear(), s.getUTCMonth() + 1, 0));
    }, [activeRange.start]);

    const { toast } = useToast();
    const exportMutation = useExportCreditUsage();
    const [isDownloading, setIsDownloading] = useState(false);
    const [exportModalOpen, setExportModalOpen] = useState(false);

    const handleExportWithDates = useCallback(
        (startDate: Date, endDate: Date) => {
            if (!organizationId) return;
            setExportModalOpen(false);
            exportMutation.mutate(
                { organizationId, startDate, endDate },
                {
                    onSuccess: async (downloadUrl) => {
                        setIsDownloading(true);
                        const { dismiss } = toast({
                            title: `Downloading usage export...`,
                            indefinite: true,
                        });
                        try {
                            // Rewrite the download URL to use the current origin so the
                            // request goes through the same host (works in prod and dev proxy).
                            const fetchUrl = new URL(downloadUrl, window.location.origin);
                            if (fetchUrl.origin !== window.location.origin) {
                                fetchUrl.href = `${window.location.origin}${fetchUrl.pathname}${fetchUrl.search}`;
                            }
                            const principal = getPrincipal();
                            const response = await fetch(fetchUrl.toString(), {
                                headers: principal ? { "X-Gitpod-Principal": `user/${principal}` } : {},
                                credentials: "same-origin",
                            });
                            if (!response.ok) {
                                throw new Error(`Download failed: ${response.status} ${response.statusText}`);
                            }
                            const blob = await response.blob();
                            const url = URL.createObjectURL(blob);
                            const link = document.createElement("a");
                            link.href = url;
                            const startISO = startDate.toISOString().slice(0, 10);
                            const endISO = endDate.toISOString().slice(0, 10);
                            link.download = `credit-usage-${startISO}_to_${endISO}.csv`;
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                            URL.revokeObjectURL(url);
                            dismiss();
                        } catch (error) {
                            dismiss();
                            toast({
                                title: "Failed to download usage export",
                                description: formatError(error),
                            });
                        } finally {
                            setIsDownloading(false);
                        }
                    },
                    onError: (error) => {
                        toast({
                            title: "Failed to export usage data",
                            description: formatError(error),
                        });
                    },
                },
            );
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps -- mutate is a stable reference in React Query v5
        [organizationId, exportMutation.mutate, toast],
    );

    const isExporting = exportMutation.isPending || isDownloading;

    const handleOpenExportModal = useCallback(() => {
        setExportModalOpen(true);
    }, []);

    useEffect(() => {
        setBreadCrumbRowAction(
            <HeaderControls
                monthRanges={monthRanges}
                selectedMonth={selectedMonth}
                onMonthChange={setSelectedMonth}
                updatedAt={updatedAt}
                onExport={handleOpenExportModal}
                isExporting={isExporting}
            />,
        );

        return () => {
            setBreadCrumbRowAction(null);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps -- setSelectedMonth is a stable nuqs setter
    }, [setBreadCrumbRowAction, monthRanges, selectedMonth, updatedAt, handleOpenExportModal, isExporting]);

    if (isLoadingMembership || isLoadingOrganization) {
        return <LoadingState />;
    }

    if (membership?.userRole !== OrganizationRole.ADMIN) {
        return <NotAuthorized />;
    }

    if (error instanceof ConnectError && error.code === Code.PermissionDenied) {
        return <NotAuthorized />;
    }

    const errorMessage = deriveErrorMessage(error);
    const dailyUsage = reportData?.dailyUsage ?? [];

    return (
        <>
            <EnterpriseUsageContent
                organizationId={organizationId}
                dailyUsage={dailyUsage}
                isLoading={isLoadingReport}
                selectedMonthLabel={activeRange.label}
                monthStart={activeRange.start}
                monthEnd={chartMonthEnd}
                selectedType={selectedType}
                onTypeChange={setSelectedType}
                grouping={grouping}
                onGroupingChange={setGrouping}
                errorMessage={errorMessage}
                cumulativeData={cumulativeData}
                isLoadingCumulative={isLoadingCumulative}
                cumulativeError={cumulativeError}
                isCurrentMonth={isCurrentMonthSelected}
                teamsEnabled={teamsEnabled}
            />
            <ExportCsvModal
                open={exportModalOpen}
                onClose={() => setExportModalOpen(false)}
                onExport={handleExportWithDates}
                isExporting={isExporting}
            />
        </>
    );
};

export default EnterpriseUsagePage;

// ─── Sub-components ─────────────────────────────────────────────────────────

import type { PlainDailyCreditUsage } from "@/queries/enterprise-usage-queries";
import type { PlainCumulativeCreditUsageResponse } from "@/queries/credit-usage-queries";
import type { MonthRange } from "@/routes/settings/organization/enterprise-usage/enterprise-usage-helpers";

function deriveErrorMessage(error: Error | null): string | undefined {
    if (!error) return undefined;
    if (error instanceof ConnectError && (error.code === Code.NotFound || error.code === Code.FailedPrecondition)) {
        return "Usage data is not available for this organization.";
    }
    return "We\u2019re having trouble loading usage data. Try selecting a different month or refreshing the page.";
}

type HeaderControlsProps = {
    monthRanges: MonthRange[];
    selectedMonth: string;
    onMonthChange: (v: string) => void;
    updatedAt: string;
    onExport: () => void;
    isExporting: boolean;
};

const HeaderControls: FC<HeaderControlsProps> = ({
    monthRanges,
    selectedMonth,
    onMonthChange,
    updatedAt,
    onExport,
    isExporting,
}) => (
    <div className="flex items-center gap-3">
        {updatedAt && (
            <Text className="hidden text-xs text-content-tertiary lg:block">
                Data through {updatedAt}
            </Text>
        )}

        <Select value={selectedMonth} onValueChange={onMonthChange}>
            <SelectTrigger
                loading={false}
                className="inline-flex w-auto min-w-0 gap-1 rounded-md border-border-light px-3 py-1.5 text-sm"
            >
                <span>{monthRanges.find((r) => r.value === selectedMonth)?.label ?? selectedMonth}</span>
            </SelectTrigger>
            <SelectContent>
                {monthRanges.map((r) => (
                    <SelectItem key={r.value} value={r.value}>
                        <span>{r.label}</span>
                    </SelectItem>
                ))}
            </SelectContent>
        </Select>

        <Button
            variant="secondary"
            size="sm"
            onClick={onExport}
            loading={isExporting}
            LeadingIcon={IconDownload}
            data-tracking-id="export-csv-enterprise-usage"
        >
            {isExporting ? "Exporting…" : "Export CSV"}
        </Button>
    </div>
);

type ContentProps = {
    organizationId: string;
    dailyUsage: PlainDailyCreditUsage[];
    isLoading: boolean;
    selectedMonthLabel: string;
    monthStart: Date;
    monthEnd: Date;
    selectedType: UsageTypeFilter;
    onTypeChange: (t: UsageTypeFilter) => void;
    grouping: Grouping;
    onGroupingChange: (g: Grouping) => void;
    errorMessage?: string;
    cumulativeData?: PlainCumulativeCreditUsageResponse;
    isLoadingCumulative: boolean;
    cumulativeError?: Error | null;
    isCurrentMonth: boolean;
    teamsEnabled: boolean;
};

const EnterpriseUsageContent: FC<ContentProps> = ({
    organizationId,
    dailyUsage,
    isLoading,
    selectedMonthLabel,
    monthStart,
    monthEnd,
    selectedType,
    onTypeChange,
    grouping,
    onGroupingChange,
    errorMessage,
    cumulativeData,
    isLoadingCumulative,
    cumulativeError,
    isCurrentMonth,
    teamsEnabled,
}) => {
    const [selectedMember, setSelectedMember] = useState<string | undefined>();
    const topUsers = useMemo(() => computeTopUsers(dailyUsage, 5), [dailyUsage]);
    const allUsers = useMemo(() => extractUniqueUsers(dailyUsage), [dailyUsage]);

    const chartData = useMemo(() => {
        if (grouping === "teams") {
            return buildTeamDailyChartData(dailyUsage, selectedType, monthStart, monthEnd);
        }
        return buildDailyChartData(dailyUsage, grouping, topUsers, {
            usageTypeFilter: selectedType,
            userFilter: selectedMember,
            monthStart,
            monthEnd,
        });
    }, [dailyUsage, grouping, topUsers, selectedType, selectedMember, monthStart, monthEnd]);

    const totalCredits = useMemo(() => computeTotalCredits(dailyUsage, "all"), [dailyUsage]);
    const environmentCredits = useMemo(() => computeTotalCredits(dailyUsage, "environment"), [dailyUsage]);
    const agenticCredits = useMemo(() => computeTotalCredits(dailyUsage, "agentic"), [dailyUsage]);

    const cumulativeTotal = cumulativeData?.orgUsage?.totalCredits ?? 0;
    const cumulativePeriodStartSeconds = cumulativeData?.periodStart?.seconds;
    const periodStartLabel = useMemo(
        () =>
            cumulativePeriodStartSeconds != null
                ? new Date(Number(cumulativePeriodStartSeconds) * 1000).toLocaleDateString(undefined, {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                      timeZone: "UTC",
                  })
                : undefined,
        [cumulativePeriodStartSeconds],
    );

    // Show "up to" bound when viewing a past month.
    const periodEndLabel = useMemo(
        () =>
            !isCurrentMonth
                ? monthEnd.toLocaleDateString(undefined, {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                      timeZone: "UTC",
                  })
                : undefined,
        [isCurrentMonth, monthEnd],
    );

    const periodLabel = periodStartLabel
        ? periodEndLabel
            ? `Since ${periodStartLabel} · Up to ${periodEndLabel}`
            : `Since ${periodStartLabel}`
        : undefined;

    const handleGroupingChange = useCallback(
        (g: Grouping) => {
            onGroupingChange(g);
            if (g === "teams") {
                setSelectedMember(undefined);
            }
        },
        [onGroupingChange],
    );

    return (
        <div className="flex flex-col gap-6" data-testid="enterprise-usage-page">
            <Text className="text-base text-content-secondary">
                Track how your organization uses credits over time.
            </Text>
            <div className="flex flex-wrap items-baseline gap-2" data-tracking-id="usage-cumulative-total">
                <span className="text-lg font-semibold text-content-primary">
                    <span>{"Cumulative total: "}</span>
                    <SkeletonText size="lg" ready={!isLoadingCumulative} className="inline-block w-24">
                        {cumulativeError ? (
                            <span className="text-content-tertiary" title="Unable to load cumulative usage">
                                &mdash;
                            </span>
                        ) : (
                            <span>
                                {cumulativeTotal > 0 ? `${formatCreditsExact(cumulativeTotal)} credits` : "\u2014"}
                            </span>
                        )}
                    </SkeletonText>
                </span>
                {periodLabel && <span className="text-sm text-content-tertiary">{periodLabel}</span>}
            </div>

            <div className="grid max-w-[780px] grid-cols-2 gap-3 sm:grid-cols-3">
                <SummaryCard
                    label="Monthly total"
                    credits={totalCredits}
                    className="col-span-2 sm:col-span-1"
                    isActive={selectedType === "all"}
                    onClick={() => onTypeChange("all")}
                    data-tracking-id="usage-filter-all"
                />
                <SummaryCard
                    label="Environment"
                    credits={environmentCredits}
                    isActive={selectedType === "environment"}
                    onClick={() => onTypeChange("environment")}
                    data-tracking-id="usage-filter-environment"
                />
                <SummaryCard
                    label="Intelligence"
                    credits={agenticCredits}
                    isActive={selectedType === "agentic"}
                    onClick={() => onTypeChange("agentic")}
                    data-tracking-id="usage-filter-intelligence"
                />
            </div>

            <EnterpriseUsageDailyChart
                chartData={chartData}
                grouping={grouping}
                onGroupingChange={handleGroupingChange}
                selectedType={selectedType}
                allUsers={allUsers}
                selectedMember={selectedMember}
                onMemberChange={setSelectedMember}
                loading={isLoading}
                monthLabel={selectedMonthLabel}
                errorMessage={errorMessage}
                showTeamsGrouping={teamsEnabled}
            />

            {teamsEnabled && cumulativeData && (
                <TeamUsageTable
                    teams={cumulativeData.teamUsage}
                    organizationId={organizationId}
                    periodLabel={periodLabel}
                    isCurrentMonth={isCurrentMonth}
                />
            )}
        </div>
    );
};

const SummaryCard: FC<{
    label: string;
    credits: number;
    className?: string;
    isActive?: boolean;
    onClick?: () => void;
    "data-tracking-id"?: string;
}> = ({ label, credits, className, isActive, onClick, "data-tracking-id": trackingId }) => (
    <div
        role={onClick ? "button" : undefined}
        tabIndex={onClick ? 0 : undefined}
        className={cn(
            "rounded-xl border border-border-light p-5 transition-colors duration-100",
            onClick && "cursor-pointer hover:bg-surface-hover",
            isActive
                ? "shadow-[0_0_0_1.5px] shadow-surface-button-primary-accent"
                : onClick
                  ? "shadow-[0_0_0_0.5px] shadow-border-base"
                  : "",
            className,
        )}
        onClick={onClick}
        onKeyDown={onClick ? (e) => e.key === "Enter" && onClick() : undefined}
        data-tracking-id={trackingId}
    >
        <Text className="text-sm text-content-secondary">{label}</Text>
        <div className="mt-1 flex items-baseline gap-1.5">
            {credits > 0 ? (
                <>
                    <span className="text-2xl font-bold text-content-primary">{formatCredits(credits)}</span>
                    <span className="text-sm text-content-tertiary">credits</span>
                </>
            ) : (
                <span className="text-2xl text-content-tertiary">&mdash;</span>
            )}
        </div>
    </div>
);
